import { generate } from 'shortid';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import FetchError from '@atlassian/jira-fetch/src/utils';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { AnalyticsAttributes } from '@atlassian/jira-product-analytics-bridge/src/types';
import {
	SUCCESS_TYPE,
	ERROR_TYPE,
	type FlagType,
} from '@atlassian/jira-servicedesk-common/src/model/flags';
import type { ErrorResponse } from '@atlassian/jira-servicedesk-common/src/utils/fetch';

import type { KnowledgeCommonState, KnowledgeCommonFlag } from '../index';
import { MONTHLY_BILLED, ANNUALLY_BILLED, INACTIVE, type BillingPeriod } from './constants';
import type { ArticleDraftState, KnowledgeCommonFlagMessage, FlagData } from './types';

const TEAM_NAME = 'jsm-nebula';

export const isNetworkErrorCode = (statusCode: number): boolean => statusCode === 0;

export const isClientErrorCode = (statusCode: number): boolean =>
	statusCode >= 400 && statusCode < 500;

export const isNetworkOrClientErrorCode = (statusCode: number): boolean =>
	isNetworkErrorCode(statusCode) || isClientErrorCode(statusCode);

export const fireErrorAnalyticsEvents = (
	error: Error | FetchError | ErrorResponse,
	id: string,
	packageName: string,
	attributes?: AnalyticsAttributes,
) => {
	// @ts-expect-error - TS2339 - Property 'statusCode' does not exist on type 'Error | FetchError | ErrorResponse'.
	if (error && typeof error.statusCode === 'number') {
		// @ts-expect-error - TS2339 - Property 'statusCode' does not exist on type 'Error | FetchError | ErrorResponse'.
		const { statusCode } = error;
		const lowPriorityError = statusCode >= 400 && statusCode < 500;
		const errorMetaId = lowPriorityError ? `${id}__4xx` : `${id}`;
		const transformedError = error instanceof FetchError ? error : new FetchError(statusCode);
		const errorObj = {
			error: transformedError,
			meta: {
				id: errorMetaId,
				packageName,
				teamName: TEAM_NAME,
			},
			...(attributes && { attributes }),
		};
		fireErrorAnalytics(errorObj);
	} else {
		fireErrorAnalytics({
			// Note there should not be a case of circular object for error , if there is it will be handled by fireErrorAnalytics
			error: error instanceof Error ? error : new Error(JSON.stringify(error)),
			meta: {
				id: `${id}__JS_ERROR`,
				packageName,
				teamName: TEAM_NAME,
			},
			...(attributes && { attributes }),
		});
	}
};

export const getPageOnDelete = (
	currentPage: number,
	updateDataSize: number,
	perPage: number,
): number => {
	const lastPage = Math.ceil(updateDataSize / perPage);
	return currentPage > lastPage ? lastPage : currentPage;
};

export const fireCustomErrorAnalytics = ({
	error,
	event,
	id,
	packageName,
	attributes,
}: {
	error: Error | FetchError | ErrorResponse;
	event?: UIAnalyticsEvent;
	id: string;
	packageName: string;
	attributes?: AnalyticsAttributes;
}) => {
	if (
		error &&
		// @ts-expect-error - TS2339 - Property 'statusCode' does not exist on type 'Error | FetchError | ErrorResponse'.
		typeof error.statusCode === 'number' &&
		// @ts-expect-error - TS2339 - Property 'statusCode' does not exist on type 'Error | FetchError | ErrorResponse'.
		!isNetworkOrClientErrorCode(error.statusCode)
	) {
		event && fireOperationalAnalytics(event, id);
	}

	fireErrorAnalyticsEvents(error, id, packageName, attributes);
};

export const getViewArticleUrl = (
	projectKey: string,
	contentId: string,
	spaceKey: string,
): string =>
	`/jira/servicedesk/projects/${projectKey}/knowledge/articles/${contentId}?spaceKey=${spaceKey}`;

export const getAllDraftsUrl = (projectKey: string): string =>
	`/jira/servicedesk/projects/${projectKey}/knowledge/drafts`;

export const getAllArticlesUrl = (projectKey: string): string =>
	`/jira/servicedesk/projects/${projectKey}/knowledge/articles`;

export const getFlag = (
	title: string,
	description: string,
	flagType: FlagType,
): KnowledgeCommonFlag => ({
	title,
	description,
	id: generate(),
	flagType,
});

export const getArticleDraftState = (state: KnowledgeCommonState): ArticleDraftState => {
	const { draftShareId, articleId, published, closed, deleted } = state;
	const isDraftPublished = Boolean(draftShareId && articleId && published && !closed);
	const isArticleSavedWithUnpublishedChanges = Boolean(
		!draftShareId && articleId && !published && closed,
	);
	const isDraftSavedWithUnpublishedChanges = Boolean(
		draftShareId && articleId && closed && !published,
	);
	const isArticleDeleted = Boolean(articleId && deleted);

	return {
		isDraftPublished,
		isDraftSavedWithUnpublishedChanges,
		isArticleSavedWithUnpublishedChanges,
		isArticleDeleted,
	};
};

export const getCommonFlagData = (
	state: KnowledgeCommonState,
	messages: KnowledgeCommonFlagMessage,
): FlagData => {
	let flag = {
		id: '',
		title: { id: '', defaultMessage: '', description: '' },
		description: { id: '', defaultMessage: '', description: '' },
		type: ERROR_TYPE,
	};
	const { showConfluenceCreatePageError } = state;

	// if confluence get content api fails
	if (showConfluenceCreatePageError) {
		flag = {
			id: 'draftErrorFlag',
			// @ts-expect-error - TS2322 - Type 'MessageDescriptorV2' is not assignable to type '{ id: string; defaultMessage: string; description: string; }'.
			title: messages.confluenceCreatePageFailureTitle,
			// @ts-expect-error - TS2322 - Type 'MessageDescriptorV2' is not assignable to type '{ id: string; defaultMessage: string; description: string; }'.
			description: messages.confluenceCreatePageFailureDescription,
			type: ERROR_TYPE,
		};
		return flag;
	}

	const {
		isDraftPublished,
		isArticleSavedWithUnpublishedChanges,
		isDraftSavedWithUnpublishedChanges,
		isArticleDeleted,
	} = getArticleDraftState(state);

	// case when new article is published --> Article View
	if (isDraftPublished) {
		flag = {
			id: 'draftPublishedFlag',
			// @ts-expect-error - TS2322 - Type 'MessageDescriptorV2' is not assignable to type '{ id: string; defaultMessage: string; description: string; }'.
			title: messages.draftSuccessFlagTitle,
			// @ts-expect-error - TS2322 - Type 'MessageDescriptorV2' is not assignable to type '{ id: string; defaultMessage: string; description: string; }'.
			description: messages.draftSuccessFlagDescription,
			// @ts-expect-error - TS2322 - Type '"SUCCESS"' is not assignable to type '"ERROR"'.
			type: SUCCESS_TYPE,
		};
	}

	// case when draft is saved with unpublished changes --> Browse All Articles Page
	if (isDraftSavedWithUnpublishedChanges) {
		flag = {
			id: 'draftSavedFlag',
			// @ts-expect-error - TS2322 - Type 'MessageDescriptorV2' is not assignable to type '{ id: string; defaultMessage: string; description: string; }'.
			title: messages.draftSuccessSavedFlagTitle,
			// @ts-expect-error - TS2322 - Type 'string | MessageDescriptorV2' is not assignable to type '{ id: string; defaultMessage: string; description: string; }'.
			description: messages.draftSuccessSavedFlagDescription || '',
			// @ts-expect-error - TS2322 - Type '"SUCCESS"' is not assignable to type '"ERROR"'.
			type: SUCCESS_TYPE,
		};
	}

	// case when older article is saved with unpublished changes --> Article View
	if (isArticleSavedWithUnpublishedChanges) {
		flag = {
			id: 'draftPublishedFlag',
			// @ts-expect-error - TS2322 - Type 'MessageDescriptorV2' is not assignable to type '{ id: string; defaultMessage: string; description: string; }'.
			title: messages.articleSuccessSavedFlagTitle,
			// @ts-expect-error - TS2322 - Type 'MessageDescriptorV2' is not assignable to type '{ id: string; defaultMessage: string; description: string; }'.
			description: messages.articleSuccessSavedFlagDescription,
			// @ts-expect-error - TS2322 - Type '"SUCCESS"' is not assignable to type '"ERROR"'.
			type: SUCCESS_TYPE,
		};
	}

	// case when article is deleted --> Browse All Articles Page
	if (isArticleDeleted) {
		flag = {
			id: 'articleDeletedFlag',
			// @ts-expect-error - TS2322 - Type 'MessageDescriptorV2' is not assignable to type '{ id: string; defaultMessage: string; description: string; }'.
			title: messages.articleDeletedFlagTitle,
			// @ts-expect-error - TS2322 - Type 'MessageDescriptorV2' is not assignable to type '{ id: string; defaultMessage: string; description: string; }'.
			description: messages.articleDeletedFlagDescription,
			// @ts-expect-error - TS2322 - Type '"SUCCESS"' is not assignable to type '"ERROR"'.
			type: SUCCESS_TYPE,
		};
	}
	return flag;
};

export const filterSupportedBillingPeriod = (billingPeriod: string): BillingPeriod => {
	if (billingPeriod === MONTHLY_BILLED) {
		return MONTHLY_BILLED;
	}
	if (billingPeriod === ANNUALLY_BILLED) {
		return ANNUALLY_BILLED;
	}
	return INACTIVE;
};

export const formatTrialEndDate = (trialEndDate?: string): Date | null => {
	if (trialEndDate != null && trialEndDate !== '') {
		return new Date(trialEndDate);
	}
	return null;
};
